/**
 * Bloqueio de preenchimento automático em campos de busca e filtro.
 *
 * Problema: o gerenciador de senhas do navegador (e extensões como LastPass,
 * 1Password, Bitwarden e Dashlane) classificam um campo de texto solto como
 * "campo de usuário" do formulário de login salvo para o domínio e injetam o
 * login/e-mail guardados assim que a tela carrega. Como esses campos estão
 * ligados por `v-model` ao termo de busca, o grid abria filtrado por um texto
 * que ninguém digitou.
 *
 * O `type="search"` do markup resolve o caso do Chrome/Edge, mas não alcança os
 * campos gerados por bibliotecas (o input interno do `vue-multiselect`, por
 * exemplo) nem as extensões de gerenciador de senhas. Este módulo fecha o cerco
 * em três camadas:
 *
 * 1. marcação — atributos de recusa reconhecidos por navegador e extensões;
 * 2. `readonly` até o primeiro foco — campo somente leitura não é preenchido
 *    automaticamente, e o atributo cai no primeiro clique/foco do usuário;
 * 3. faxina — se ainda assim algo for injetado, o evento de animação disparado
 *    pelo estado `:-webkit-autofill` devolve o campo a vazio e avisa o `v-model`.
 *
 * Só atua nos campos de busca/filtro: os formulários de login e de cadastro
 * continuam aceitando o preenchimento automático normalmente.
 */

const SELETOR = [
    'input[type="search"]',
    '.multiselect__input',   // vue-multiselect
    '.multiselect-search',   // @vueform/multiselect
    '[data-busca]',          // marcação manual, para casos fora do padrão
].join(',');

const ANIMACAO = 'miraiAntiPreenchimento';
const MARCA = '__miraiAntiPreenchimento';

let instalado = false;

function injetarEstiloDeDeteccao() {
    const estilo = document.createElement('style');
    estilo.id = 'mirai-anti-preenchimento';
    // O Chrome/Edge aplicam :-webkit-autofill ao campo preenchido automaticamente.
    // A animação não muda nada na tela: serve só como gatilho de evento em JS.
    estilo.textContent = `
@keyframes ${ANIMACAO} { from { outline-color: inherit; } to { outline-color: inherit; } }
input:-webkit-autofill { animation-name: ${ANIMACAO}; animation-duration: 0.01s; }`;
    document.head.appendChild(estilo);
}

function limpar(campo) {
    if (!campo.value) return;
    campo.value = '';
    // Sincroniza o v-model do Vue, que escuta input/change.
    campo.dispatchEvent(new Event('input', { bubbles: true }));
    campo.dispatchEvent(new Event('change', { bubbles: true }));
}

function proteger(campo) {
    if (campo[MARCA]) return;
    campo[MARCA] = true;

    campo.setAttribute('autocomplete', 'off');
    campo.setAttribute('autocapitalize', 'off');
    campo.setAttribute('autocorrect', 'off');
    campo.setAttribute('spellcheck', 'false');
    campo.setAttribute('data-lpignore', 'true');    // LastPass
    campo.setAttribute('data-1p-ignore', '');       // 1Password
    campo.setAttribute('data-bwignore', '');        // Bitwarden
    campo.setAttribute('data-form-type', 'other');  // Dashlane

    // Nome genérico ("search", "login", "usuario") alimenta a heurística do
    // navegador; um nome próprio do campo não casa com nenhuma credencial.
    const nome = campo.getAttribute('name') || '';
    if (!nome || /search|busca|user|usuario|login|email|mail/i.test(nome)) {
        campo.setAttribute('name', `mirai-busca-${Math.random().toString(36).slice(2, 10)}`);
    }

    // Campo somente leitura não é alvo de preenchimento automático. O atributo
    // sai no primeiro foco (clique, Tab ou foco disparado por código), então o
    // usuário nunca percebe a diferença ao digitar.
    if (!campo.readOnly) {
        campo.setAttribute('readonly', 'readonly');
        const liberar = () => campo.removeAttribute('readonly');
        // pointerdown/touchstart vêm antes do focus: no celular isso garante que
        // o teclado abra já no primeiro toque.
        campo.addEventListener('pointerdown', liberar, { once: true });
        campo.addEventListener('touchstart', liberar, { once: true, passive: true });
        campo.addEventListener('focus', liberar, { once: true });
        if (document.activeElement === campo) liberar();
    }
}

function varrer() {
    document.querySelectorAll(SELETOR).forEach(proteger);
}

export function instalarAntiPreenchimento() {
    if (instalado || typeof document === 'undefined') return;
    instalado = true;

    injetarEstiloDeDeteccao();

    document.addEventListener('animationstart', (evento) => {
        if (evento.animationName !== ANIMACAO) return;
        const campo = evento.target;
        if (campo instanceof HTMLInputElement && campo.matches(SELETOR)) limpar(campo);
    }, true);

    varrer();

    // Telas montadas depois (troca de rota, modal, dropdown do multiselect).
    let agendado = null;
    const observador = new MutationObserver(() => {
        if (agendado) return;
        agendado = setTimeout(() => {
            agendado = null;
            varrer();
        }, 50);
    });
    observador.observe(document.documentElement, { childList: true, subtree: true });
}

export default instalarAntiPreenchimento;
